import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { useLang } from "../../lib/i18n";
import { useFetch } from "../../lib/useFetch";
import { eur, fmtDate } from "../../lib/api";
import { EventCard } from "../../components/site/EventCard";
import { StatusBadge } from "../../components/site/FileUpload";

export const H = ({ children, sub }) => (
  <div className="mb-8"><h1 className="font-display text-3xl font-extrabold text-[#0A2540] md:text-4xl">{children}</h1>{sub && <p className="mt-2 text-slate-500">{sub}</p>}</div>
);

export function Overview() {
  const { user } = useAuth();
  const { t, tr, ts, lang } = useLang();
  const { data: regs } = useFetch("/me/registrations");
  const { data: msgs } = useFetch("/me/messages");
  const active = (regs || []).filter((r) => r.status !== "cancelled" && r.event?.status !== "Completed");
  const due = active.reduce((a, r) => a + r.balance, 0);
  return (
    <div data-testid="account-overview">
      <H sub={user.email}>{t("welcome")}, {user.first_name}</H>
      <div className="grid gap-4 sm:grid-cols-3">
        {[[t("acc_regs"), active.length, "stat-registrations"], [t("balance"), eur(due), "stat-balance"], [t("acc_msgs"), (msgs || []).length, "stat-messages"]].map(([l, v, id]) => (
          <div key={id} className="rounded-3xl border border-slate-200 bg-white p-6"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{l}</p><p className="mt-2 font-display text-3xl font-extrabold text-[#0A2540]" data-testid={id}>{v}</p></div>
        ))}
      </div>
      <div className="mt-10 space-y-3">
        {active.map((r) => (
          <Link key={r.id} to="/account/registrations" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-5 hover:border-[#00A8B5]">
            <div><p className="font-display font-bold text-[#0A2540]">{tr(r.event, "title")}</p><p className="text-sm text-slate-500">{r.participant?.first_name} · {fmtDate(r.event.start_date, lang)} · {r.event.location?.name}</p></div>
            <div className="flex gap-2"><StatusBadge status={r.status} label={ts(r.status)} /><StatusBadge status={r.payment_status} label={ts(r.payment_status)} /></div>
          </Link>
        ))}
        <Link to="/account/clinics" className="inline-flex items-center gap-2 pt-2 font-semibold text-[#FF5722]">{t("book_clinic")}<ArrowRight size={16} /></Link>
      </div>
    </div>
  );
}

export function AccountClinics() {
  const { t } = useLang();
  const { user } = useAuth();
  const { data } = useFetch("/events");
  const evs = (data || []).filter((e) => e.status !== "Completed");
  const interests = (user.participants || []).flatMap((p) => p.interest_classes || []);
  const mine = evs.filter((e) => (e.class_names || [e.class_name]).some((c) => interests.includes(c)));
  const rest = evs.filter((e) => !mine.includes(e));
  return (
    <div data-testid="account-clinics">
      <H>{t("acc_events")}</H>
      {mine.length > 0 && <><p className="eyebrow mb-4">{t("interests").split("(")[0]}</p><div className="mb-10 grid gap-6 md:grid-cols-2">{mine.map((e) => <EventCard key={e.id} e={e} />)}</div></>}
      <div className="grid gap-6 md:grid-cols-2">{rest.map((e) => <EventCard key={e.id} e={e} />)}</div>
    </div>
  );
}

export function History() {
  const { t, tr, ts, lang } = useLang();
  const { data } = useFetch("/me/registrations");
  const past = (data || []).filter((r) => r.event?.status === "Completed" || r.event?.end_date < new Date().toISOString().slice(0, 10));
  return (
    <div data-testid="account-history">
      <H>{t("acc_history")}</H>
      {!past.length && <p className="text-slate-500">{t("no_items")}</p>}
      <ol className="relative space-y-6 border-l-2 border-[#00A8B5]/30 pl-8">
        {past.map((r) => (
          <li key={r.id} className="relative" data-testid={`history-item-${r.id}`}>
            <span className="absolute -left-[41px] top-1 h-4 w-4 rounded-full border-4 border-white bg-[#00A8B5] ring-2 ring-[#00A8B5]/30" />
            <p className="text-xs font-bold uppercase text-[#00A8B5]">{fmtDate(r.event.start_date, lang)}</p>
            <p className="font-display text-lg font-bold text-[#0A2540]">{tr(r.event, "title")}</p>
            <p className="text-sm text-slate-500">{r.participant?.first_name} {r.participant?.last_name} · {r.class_name || r.event.class_name} · {r.event.location?.name} · {ts(r.status)}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function Messages() {
  const { t, lang } = useLang();
  const { data } = useFetch("/me/messages");
  return (
    <div data-testid="account-messages">
      <H>{t("acc_msgs")}</H>
      {!data?.length && <p className="text-slate-500">{t("no_items")}</p>}
      <div className="space-y-3">
        {(data || []).map((m) => (
          <details key={m.id} className="group rounded-2xl border border-slate-200 bg-white p-5" data-testid={`message-${m.id}`}>
            <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
              <span className="font-semibold text-[#0A2540]">{m.subject}</span><span className="shrink-0 text-xs text-slate-400">{fmtDate(m.sent_at, lang)}</span>
            </summary>
            <p className="mt-4 whitespace-pre-line break-words text-sm leading-relaxed text-slate-600">
              {m.body.split(/(https:\/\/[^\s)]*[^\s).,;:!?])/g).map((part, i) => (part.startsWith("https://")
                ? <a key={i} href={part} target="_blank" rel="noreferrer" className="font-semibold text-[#FF5722] underline" data-testid={`message-link-${m.id}-${i}`}>{part}</a> : part))}
            </p>
          </details>
        ))}
      </div>
    </div>
  );
}
